import { translations } from '@internal/i18n/translations'
import { RadioButtonGroup } from '@renderer-ui-library/atoms'
import { useFilterBarOptions } from '@renderer-ui-library/molecules'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@renderer-ui-library/mui'
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useState } from 'react'
import { tracker } from 'utils/tracking/tracker'
import { SearchResultsSortKey } from './SearchResultsSortKey'
import styles from './searchResultsSorting.module.scss'
import { useSearchParams } from './useSearchParams'
import { useSearchResultsRouting } from './useSearchResultsRouting'

interface Props {
  onChange?: () => void
  closeMobileMenu: () => void
}

export const SearchResultsSorting: React.FC<Props> = React.memo((props) => {
  const { sort, ...searchParams } = useSearchParams()
  const { gotoSearchResults } = useSearchResultsRouting()
  const { t } = useTranslation(localeNamespace.common)
  const { isMobile } = useMediaQuery()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const { options } = useFilterBarOptions()

  const [currentSelectedValue, setCurrentSelectedValue] = useState(sort)

  const handleClose = useCallback(() => setAnchorEl(null), [])

  const handleChangeSort = useCallback(
    (sort: string) => {
      const sortKey = sort as SearchResultsSortKey
      props.closeMobileMenu()
      handleClose()
      props.onChange?.()

      tracker.trackEvent({
        name: 'machine-sorting',
        machine_sorting_order: sortKey,
        is_interaction: true,
      })

      gotoSearchResults({
        ...searchParams,
        sort: sortKey,
        replace: true,
      })

      return setCurrentSelectedValue(sort as SearchResultsSortKey)
    },
    [props, handleClose, gotoSearchResults, searchParams]
  )

  return (
    <>
      {isMobile ? (
        <RadioButtonGroup
          options={options}
          value={sort}
          onChange={handleChangeSort}
        />
      ) : (
        <div className={styles.container}>
          <FormControl fullWidth sx={{ width: '250px' }} size='small'>
            <InputLabel id='popover-sort-label'>
              {t(translations.searchResultsSortingDescription)}
            </InputLabel>
            <Select
              id='popover-sort'
              labelId='popover-sort-label'
              displayEmpty
              label={t(translations.searchResultsSortingDescription)}
              value={currentSelectedValue}
              variant='outlined'
              onChange={(event) =>
                handleChangeSort(event.target.value as SearchResultsSortKey)
              }
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    component='span'
                  >
                    {option.label}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}
    </>
  )
})

SearchResultsSorting.displayName = 'SearchResultsSorting'
